import {
	doc,
	getDoc,
	query,
	collection,
	where,
	getDocs,
	addDoc,
	updateDoc,
	deleteDoc,
	runTransaction,
	onSnapshot,
} from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { callFirebaseFunction } from '../../config/firebaseFunctions';
import {
	Unit,
	PropertyGroupMembership,
} from '../../types/Property.types';
import { PropertyGroup, Property } from '../Slices/propertyDataSlice';
import { apiSlice, docToData } from './apiSlice';
import {
	filterRecordsByAccessProperties,
	resolveAccountAccessContext,
	resolveTargetUserId,
	type AccountAccessContext,
} from './accountContext';
import {
	canPropertyGroups,
	getEffectiveAccessPlanId,
	getMaxPropertiesForPlan,
} from '../../utils/subscriptionUtils';
import type { SubscriptionData } from '../../utils/subscriptionUtils';
import { trackAnalyticsEvent } from '../../analytics/analytics';

const PROPERTY_GROUP_MEMBERSHIPS_COLLECTION = 'propertyGroupMemberships';

const removeUndefinedFieldsDeep = <T,>(value: T): T => {
	if (value === undefined) return value;
	if (value === null) return value;
	if (value instanceof Date) return value;

	if (Array.isArray(value)) {
		return value
			.map((item) => removeUndefinedFieldsDeep(item))
			.filter((item) => item !== undefined) as T;
	}

	if (typeof value === 'object') {
		const prototype = Object.getPrototypeOf(value);
		if (prototype && prototype !== Object.prototype) {
			return value;
		}

		const cleanedEntries = Object.entries(value as Record<string, unknown>)
			.filter(([, fieldValue]) => fieldValue !== undefined)
			.map(([key, fieldValue]) => [
				key,
				removeUndefinedFieldsDeep(fieldValue),
			])
			.filter(([, fieldValue]) => fieldValue !== undefined);

		return Object.fromEntries(cleanedEntries) as T;
	}

	return value;
};

const assertCanManagePropertyGroups = async (accountId: string) => {
	const accountOwnerDoc = await getDoc(doc(db, 'users', accountId));
	const accountOwnerData = accountOwnerDoc.data() || {};
	const subscription = accountOwnerData.subscription as SubscriptionData | undefined;

	if (!subscription || !canPropertyGroups(subscription)) {
		throw new Error(
			'Property groups are available on Property and Portfolio plans.',
		);
	}
};

const fetchPropertiesByIds = async (propertyIds: string[]): Promise<Property[]> => {
	if (propertyIds.length === 0) {
		return [];
	}

	const propertiesById = new Map<string, Property>();
	for (let i = 0; i < propertyIds.length; i += 10) {
		const batch = propertyIds.slice(i, i + 10);
		try {
			const propertiesQuery = query(
				collection(db, 'properties'),
				where('__name__', 'in', batch),
			);
			const propertiesSnapshot = await getDocs(propertiesQuery);
			const batchProperties = propertiesSnapshot.docs
				.map((propertyDoc) => docToData(propertyDoc) as Property)
				.filter(Boolean) as Property[];
			batchProperties.forEach((property) => {
				if (property?.id) {
					propertiesById.set(property.id, property);
				}
			});
		} catch (error) {
			console.warn('Could not fetch property batch by ids:', {
				batchSize: batch.length,
				error,
			});

			// Fallback to per-document reads so one unauthorized document does not
			// fail the entire batch and hide authorized properties.
			for (const propertyId of batch) {
				try {
					const propertyDoc = await getDoc(doc(db, 'properties', propertyId));
					const property = docToData(propertyDoc) as Property;
					if (property?.id) {
						propertiesById.set(property.id, property);
					}
				} catch (singleError) {
					console.warn('Could not fetch property by id fallback:', {
						propertyId,
						error: singleError,
					});
				}
			}
		}
	}

	return Array.from(propertiesById.values());
};

const scopeGroupsToAccessContext = async (
	groups: PropertyGroup[],
	accessContext: AccountAccessContext,
	targetUserId: string,
): Promise<PropertyGroup[]> => {
	const linkedPropertyIds = new Set(accessContext.allowedPropertyIds);
	if (linkedPropertyIds.size === 0) {
		return [];
	}

	const scopedGroups = groups
		.map((group) => ({
			...group,
			properties:
				group.properties?.filter((property) =>
					linkedPropertyIds.has(property.id),
				) || [],
		}))
		.filter((group) => group.properties && group.properties.length > 0);

	if (scopedGroups.length > 0) {
		return scopedGroups;
	}

	const assignedProperties = await fetchPropertiesByIds(
		Array.from(linkedPropertyIds),
	);
	if (assignedProperties.length === 0) {
		return [];
	}

	return [
		{
			id: `team-${targetUserId}-assigned-properties`,
			name: 'Assigned Properties',
			userId: targetUserId,
			accountId: targetUserId,
			properties: assignedProperties,
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
		},
	];
};

const upsertPropertyGroupMembership = async (params: {
	accountId: string;
	groupId: string;
	propertyId: string;
	sortOrder?: number;
}) => {
	const { accountId, groupId, propertyId, sortOrder = Date.now() } = params;
	const now = new Date().toISOString();

	const membershipQuery = query(
		collection(db, PROPERTY_GROUP_MEMBERSHIPS_COLLECTION),
		where('propertyId', '==', propertyId),
		where('accountId', '==', accountId),
	);
	const membershipSnapshot = await getDocs(membershipQuery);
	const existingMembershipDoc = membershipSnapshot.docs[0];

	if (existingMembershipDoc) {
		await updateDoc(existingMembershipDoc.ref, {
			groupId,
			sortOrder,
			updatedAt: now,
		});
		return;
	}

	await addDoc(collection(db, PROPERTY_GROUP_MEMBERSHIPS_COLLECTION), {
		accountId,
		groupId,
		propertyId,
		sortOrder,
		createdAt: now,
		updatedAt: now,
	});
};

const deletePropertyGroupMemberships = async (
	propertyId: string,
	accountId?: string,
) => {
	let membershipQuery = query(
		collection(db, PROPERTY_GROUP_MEMBERSHIPS_COLLECTION),
		where('propertyId', '==', propertyId),
	);

	if (accountId) {
		membershipQuery = query(
			collection(db, PROPERTY_GROUP_MEMBERSHIPS_COLLECTION),
			where('propertyId', '==', propertyId),
			where('accountId', '==', accountId),
		);
	}

	const membershipSnapshot = await getDocs(membershipQuery);
	for (const membershipDoc of membershipSnapshot.docs) {
		await deleteDoc(membershipDoc.ref);
	}
};

const propertySlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		// Property endpoints
		// Property Group endpoints
		getPropertyGroups: builder.query<PropertyGroup[], void>({
			async queryFn() {
				try {
					const currentUser = auth.currentUser;
					if (!currentUser) {
						return { error: 'User not authenticated' };
					}
					const userId = currentUser.uid;
					// Get user's email for shared properties lookup
					const userDocRef = doc(db, 'users', userId);
					const userDoc = await getDoc(userDocRef);
					const userData = userDoc.data();
					const userEmail = userData?.email;

					if (
						String(userData?.role || '').trim().toLowerCase() === 'tenant'
					) {
						try {
							await callFirebaseFunction<
								Record<string, never>,
								{ success: boolean }
							>('syncTenantAccessFromInvites', {});
						} catch (syncError) {
							console.warn('Tenant access sync in property query skipped:', syncError);
						}
					}

					const accessContext = await resolveAccountAccessContext();
					const accessibleAccountIds = accessContext.accountIds;
					const targetUserId = await resolveTargetUserId();
					const isTeamMemberScoped = accessContext.isScopedTeamMember;

					// Get property groups
					const groupDocs = [] as any[];
					for (const accountId of accessibleAccountIds) {
						const groupsQuery = query(
							collection(db, 'propertyGroups'),
							where('accountId', '==', accountId),
						);
						const querySnapshot = await getDocs(groupsQuery);
						groupDocs.push(...querySnapshot.docs);
					}

					const uniqueGroupDocs = Array.from(
						new Map(
							groupDocs.map((groupDoc) => [groupDoc.id, groupDoc]),
						).values(),
					);

					const groups = uniqueGroupDocs
						.map((doc) => docToData(doc) as PropertyGroup)
						.filter(Boolean) as PropertyGroup[];

					const memberships: PropertyGroupMembership[] = [];
					for (const accountId of accessibleAccountIds) {
						try {
							const membershipsQuery = query(
								collection(db, PROPERTY_GROUP_MEMBERSHIPS_COLLECTION),
								where('accountId', '==', accountId),
							);
							const membershipsSnapshot = await getDocs(membershipsQuery);
							const membershipBatch = membershipsSnapshot.docs
								.map(
									(doc) => docToData(doc) as PropertyGroupMembership,
								)
								.filter(Boolean) as PropertyGroupMembership[];
							memberships.push(...membershipBatch);
						} catch (_membershipError) {
							// Graceful fallback for environments where propertyGroupMemberships
							// rules have not been deployed yet.
						}
					}

					const membershipsByGroupId = new Map<string, PropertyGroupMembership[]>();
					for (const membership of memberships) {
						if (!membership.groupId || !membership.propertyId) {
							continue;
						}

						const current = membershipsByGroupId.get(membership.groupId) || [];
						current.push(membership);
						membershipsByGroupId.set(membership.groupId, current);
					}

					for (const [groupId, groupMemberships] of membershipsByGroupId.entries()) {
						groupMemberships.sort((a, b) => {
							const aOrder = Number.isFinite(a.sortOrder)
								? a.sortOrder
								: Number.MAX_SAFE_INTEGER;
							const bOrder = Number.isFinite(b.sortOrder)
								? b.sortOrder
								: Number.MAX_SAFE_INTEGER;
							return aOrder - bOrder;
						});
						membershipsByGroupId.set(groupId, groupMemberships);
					}

					const membershipPropertyIds = Array.from(
						new Set(memberships.map((membership) => membership.propertyId).filter(Boolean)),
					);
					const membershipProperties = await fetchPropertiesByIds(membershipPropertyIds);
					const propertyById = new Map(
						membershipProperties.map((property) => [property.id, property]),
					);

					const normalizedUserEmail = String(userEmail || '').trim().toLowerCase();

					let tenantInvitePropertyIds: string[] = [];
					if (normalizedUserEmail) {
						try {
							const tenantInvitesQuery = query(
								collection(db, 'tenantInvitationCodes'),
								where('tenantEmail', '==', normalizedUserEmail),
							);
							const tenantInvitesSnapshot = await getDocs(tenantInvitesQuery);
							const invitePropertyIds = tenantInvitesSnapshot.docs
								.map((inviteDoc) => inviteDoc.data() as any)
								.filter((invite) => {
									const status = String(invite?.status || '').trim().toLowerCase();
									return (
										(status === 'active' || status === 'redeemed') &&
										typeof invite?.propertyId === 'string' &&
										invite.propertyId.trim().length > 0
									);
								})
								.map((invite) => String(invite.propertyId).trim());

							tenantInvitePropertyIds = Array.from(new Set(invitePropertyIds));
						} catch (tenantInviteError) {
							console.warn(
								'Could not fetch tenant invitation code assignments:',
								tenantInviteError,
							);
						}
					}

					// Fetch properties for each group
					const groupsWithProperties: PropertyGroup[] = await Promise.all(
						groups.map(async (group) => {
							const groupMemberships = membershipsByGroupId.get(group.id) || [];
							let ownedProperties = groupMemberships
								.map((membership) => propertyById.get(membership.propertyId))
								.filter(Boolean) as Property[];

							// Legacy fallback for records that still only rely on property.groupId
							if (ownedProperties.length === 0) {
								const legacyQueryClauses = [where('groupId', '==', group.id)];
								if (group.accountId) {
									legacyQueryClauses.push(where('accountId', '==', group.accountId));
								}
								const propertiesQuery = query(
									collection(db, 'properties'),
									...legacyQueryClauses,
								);
								const propertiesSnapshot = await getDocs(propertiesQuery);
								ownedProperties = propertiesSnapshot.docs
									.map((doc) => docToData(doc) as Property)
									.filter(Boolean) as Property[];
							}

							const uniqueProperties = Array.from(
								new Map(ownedProperties.map((property) => [property.id, property])).values(),
							);

							return {
								...group,
								properties: uniqueProperties,
							};
						}),
					);

					let finalGroups: PropertyGroup[] = [...groupsWithProperties];

					if (String(userData?.role || '').trim().toLowerCase() === 'tenant') {
						const tenantScopedIds = new Set<string>(tenantInvitePropertyIds);

						if (normalizedUserEmail) {
							for (const group of finalGroups) {
								for (const property of group.properties || []) {
									const tenants = (((property as any).tenants as any[]) || []).filter(
										Boolean,
									);
									const hasTenantMatch = tenants.some(
										(tenant) =>
											String(tenant?.email || '').trim().toLowerCase() ===
											normalizedUserEmail,
									);
									if (hasTenantMatch && property?.id) {
										tenantScopedIds.add(property.id);
									}
								}
							}
						}

						let tenantAssignedProperties = finalGroups
							.flatMap((group) => group.properties || [])
							.filter((property) => property?.id && tenantScopedIds.has(property.id));

						if (tenantAssignedProperties.length === 0 && tenantScopedIds.size > 0) {
							tenantAssignedProperties = await fetchPropertiesByIds(
								Array.from(tenantScopedIds),
							);
						}

						const uniqueTenantProperties = Array.from(
							new Map(
								tenantAssignedProperties.map((property) => [property.id, property]),
							).values(),
						);

						if (uniqueTenantProperties.length > 0) {
							const myPropertiesGroup = finalGroups.find(
								(group) => group.name?.toLowerCase() === 'my properties',
							);

							if (myPropertiesGroup) {
								myPropertiesGroup.properties = uniqueTenantProperties;
								finalGroups = finalGroups
									.filter((group) => group.id === myPropertiesGroup.id)
									.map((group) => ({ ...group }));
							} else {
								finalGroups = [
									{
										id: `tenant-${targetUserId}-assigned-properties`,
										name: 'Assigned Properties',
										userId: targetUserId,
										accountId: targetUserId,
										properties: uniqueTenantProperties,
										createdAt: new Date().toISOString(),
										updatedAt: new Date().toISOString(),
									},
								];
							}
						} else {
							finalGroups = [];
						}
					}

					const isTenantUser =
						String(userData?.role || '').trim().toLowerCase() === 'tenant';

					if (isTeamMemberScoped && !isTenantUser) {
						finalGroups = await scopeGroupsToAccessContext(
							finalGroups,
							accessContext,
							targetUserId,
						);
					}

					// Final fallback: if groups are missing but properties exist, expose a virtual group
					if (finalGroups.length === 0 && !isTenantUser && !isTeamMemberScoped) {
						const fallbackProperties: Property[] = [];
						for (const accountId of accessibleAccountIds) {
							const accountPropertiesQuery = query(
								collection(db, 'properties'),
								where('accountId', '==', accountId),
							);
							const accountPropertiesSnapshot = await getDocs(
								accountPropertiesQuery,
							);
							const batch = accountPropertiesSnapshot.docs
								.map((doc) => docToData(doc) as Property)
								.filter(Boolean) as Property[];
							fallbackProperties.push(...batch);
						}

						const uniqueFallbackProperties = Array.from(
							new Map(
								fallbackProperties.map((property) => [property.id, property]),
							).values(),
						) as Property[];

						if (uniqueFallbackProperties.length > 0) {
							finalGroups.push({
								id: `virtual-${targetUserId}-my-properties`,
								name: 'Ungrouped Properties',
								userId: targetUserId,
								accountId: targetUserId,
								properties: uniqueFallbackProperties,
								createdAt: new Date().toISOString(),
								updatedAt: new Date().toISOString(),
							});
						}
					}

					return { data: finalGroups };
				} catch (error: any) {
					return { error: error.message };
				}
			},
			providesTags: ['PropertyGroups', 'Properties'],
		}),

		getPropertyGroup: builder.query<PropertyGroup, string>({
			async queryFn(groupId: string) {
				try {
					const docRef = doc(db, 'propertyGroups', groupId);
					const docSnapshot = await getDoc(docRef);
					const data = docToData(docSnapshot) as PropertyGroup;
					return { data };
				} catch (error: any) {
					return { error: error.message };
				}
			},
			providesTags: ['PropertyGroups'],
		}),

		createPropertyGroup: builder.mutation<
			PropertyGroup,
			Omit<PropertyGroup, 'id'>
		>({
			async queryFn(newGroup) {
				try {
					const currentUser = auth.currentUser;
					if (!currentUser) {
						return { error: 'User not authenticated' };
					}
					const targetUserId = await resolveTargetUserId();
					const accessContext = await resolveAccountAccessContext();
					await assertCanManagePropertyGroups(targetUserId);

					if (accessContext.isScopedTeamMember) {
						return {
							error:
								'Team member accounts cannot create property groups. Ask the account owner to manage property groups.',
						};
					}

					const docRef = await addDoc(collection(db, 'propertyGroups'), {
						...newGroup,
						userId: targetUserId, // Ensure property group is owned by account owner
						accountId: targetUserId,
						createdAt: new Date().toISOString(),
						updatedAt: new Date().toISOString(),
					});
					return {
						data: {
							id: docRef.id,
							...newGroup,
							userId: targetUserId,
							accountId: targetUserId,
						} as PropertyGroup,
					};
				} catch (error: any) {
					return { error: error.message };
				}
			},
			invalidatesTags: ['PropertyGroups'],
		}),

		updatePropertyGroup: builder.mutation<
			PropertyGroup,
			{ id: string; updates: Partial<PropertyGroup> }
		>({
			async queryFn({ id, updates }) {
				try {
					const docRef = doc(db, 'propertyGroups', id);
					const groupSnapshot = await getDoc(docRef);
					const accountId = String(groupSnapshot.data()?.accountId || '').trim();
					if (accountId) {
						await assertCanManagePropertyGroups(accountId);
					}
					await updateDoc(docRef, {
						...updates,
						updatedAt: new Date().toISOString(),
					});
					return { data: { id, ...updates } as PropertyGroup };
				} catch (error: any) {
					return { error: error.message };
				}
			},
			invalidatesTags: ['PropertyGroups'],
		}),

		deletePropertyGroup: builder.mutation<void, string>({
			async queryFn(groupId: string) {
				try {
					const groupSnapshot = await getDoc(doc(db, 'propertyGroups', groupId));
					const accountId = String(groupSnapshot.data()?.accountId || '').trim();
					if (accountId) {
						await assertCanManagePropertyGroups(accountId);
					}
					await callFirebaseFunction<
						{ groupId: string },
						{ success: boolean }
					>('deletePropertyGroupCascade', { groupId });
					return { data: undefined };
				} catch (error: any) {
					return { error: error.message };
				}
			},
			invalidatesTags: ['PropertyGroups', 'Properties'],
		}),

		// Property endpoints
		getProperties: builder.query<Property[], void>({
			async queryFn() {
				try {
					// Get authenticated user from Firebase Auth
					const currentUser = auth.currentUser;
					if (!currentUser) {
						return { error: 'User not authenticated' };
					}
					const userId = currentUser.uid;

					// Get user's email for shared properties lookup
					const userDocRef = doc(db, 'users', userId);
					const userDoc = await getDoc(userDocRef);
					const userData = userDoc.data();
					const userEmail = userData?.email;
					const normalizedUserEmail = String(userEmail || '')
						.trim()
						.toLowerCase();

					if (
						String(userData?.role || '').trim().toLowerCase() === 'tenant'
					) {
						try {
							await callFirebaseFunction<
								Record<string, never>,
								{ success: boolean }
							>('syncTenantAccessFromInvites', {});
						} catch (syncError) {
							console.warn('Tenant access sync in getProperties skipped:', syncError);
						}
					}
					const accessContext = await resolveAccountAccessContext();
					const accessibleAccountIds = accessContext.accountIds;

					let tenantInviteProperties: Property[] = [];
					if (normalizedUserEmail) {
						try {
							const tenantInvitesQuery = query(
								collection(db, 'tenantInvitationCodes'),
								where('tenantEmail', '==', normalizedUserEmail),
							);
							const tenantInvitesSnapshot = await getDocs(tenantInvitesQuery);
							const tenantInvitePropertyIds = tenantInvitesSnapshot.docs
								.map((inviteDoc) => inviteDoc.data() as any)
								.filter((invite) => {
									const status = String(invite?.status || '').trim().toLowerCase();
									return (
										(status === 'active' || status === 'redeemed') &&
										typeof invite?.propertyId === 'string' &&
										invite.propertyId.trim().length > 0
									);
								})
								.map((invite) => String(invite.propertyId).trim());

							if (tenantInvitePropertyIds.length > 0) {
								tenantInviteProperties = await fetchPropertiesByIds(
									Array.from(new Set(tenantInvitePropertyIds)),
								);
							}
						} catch (tenantInviteError) {
							console.warn(
								'Could not fetch tenant invite assigned properties:',
								tenantInviteError,
							);
						}
					}

					const accountGroupIds = new Map<string, string[]>();
					for (const accountId of accessibleAccountIds) {
						const groupsQuery = query(
							collection(db, 'propertyGroups'),
							where('accountId', '==', accountId),
						);
						const groupsSnapshot = await getDocs(groupsQuery);
						accountGroupIds.set(
							accountId,
							groupsSnapshot.docs.map((groupDoc) => groupDoc.id),
						);
					}

					const ownedProperties: Property[] = [];
					for (const [accountId, groupIds] of accountGroupIds.entries()) {
						if (groupIds.length === 0) {
							continue;
						}

						// Process in batches of 10 (Firestore limitation)
						for (let i = 0; i < groupIds.length; i += 10) {
							const batch = groupIds.slice(i, i + 10);
							try {
								const propertiesQuery = query(
									collection(db, 'properties'),
									where('accountId', '==', accountId),
									where('groupId', 'in', batch),
								);
								const propertiesSnapshot = await getDocs(propertiesQuery);
								const properties = propertiesSnapshot.docs
									.map((doc) => docToData(doc) as Property)
									.filter(Boolean) as Property[];
								ownedProperties.push(...properties);
							} catch (groupQueryError) {
								console.warn(
									'Could not fetch group-linked properties batch:',
									groupQueryError,
								);
							}
						}
					}

					const accountProperties: Property[] = [];
					for (const accountId of accessibleAccountIds) {
						const accountPropertiesQuery = query(
							collection(db, 'properties'),
							where('accountId', '==', accountId),
						);
						const accountPropertiesSnapshot = await getDocs(
							accountPropertiesQuery,
						);
						const accountPropertiesBatch = accountPropertiesSnapshot.docs
							.map((doc) => docToData(doc) as Property)
							.filter(Boolean) as Property[];
						accountProperties.push(...accountPropertiesBatch);
					}

					// Also fetch properties where user is a co-owner
					let coOwnerProperties: Property[] = [];
					for (const accountId of accessibleAccountIds) {
						try {
							const coOwnerPropertiesQuery = query(
								collection(db, 'properties'),
								where('accountId', '==', accountId),
								where('coOwners', 'array-contains', userId),
							);
							const coOwnerPropertiesSnapshot = await getDocs(
								coOwnerPropertiesQuery,
							);
							const coOwnerPropertiesBatch = coOwnerPropertiesSnapshot.docs
								.map((doc) => docToData(doc) as Property)
								.filter(Boolean) as Property[];
							coOwnerProperties.push(...coOwnerPropertiesBatch);
						} catch (coOwnerError) {
							console.warn('Could not fetch co-owner properties:', coOwnerError);
						}
					}

					// Also fetch properties where user is an administrator
					let adminProperties: Property[] = [];
					for (const accountId of accessibleAccountIds) {
						try {
							const adminPropertiesQuery = query(
								collection(db, 'properties'),
								where('accountId', '==', accountId),
								where('administrators', 'array-contains', userId),
							);
							const adminPropertiesSnapshot = await getDocs(adminPropertiesQuery);
							const adminPropertiesBatch = adminPropertiesSnapshot.docs
								.map((doc) => docToData(doc) as Property)
								.filter(Boolean) as Property[];
							adminProperties.push(...adminPropertiesBatch);
						} catch (adminError) {
							console.warn('Could not fetch admin properties:', adminError);
						}
					}

					// Combine and deduplicate
					const allProperties = [
						...accountProperties,
						...ownedProperties,
						...coOwnerProperties,
						...adminProperties,
						...tenantInviteProperties,
					];
					const uniqueProperties = Array.from(
						new Map(allProperties.map((p) => [p.id, p])).values(),
					);
					const returnedProperties = filterRecordsByAccessProperties(
						uniqueProperties,
						accessContext,
						(property) => property.id,
					);

					return { data: returnedProperties };
				} catch (error: any) {
					console.error('Error fetching properties:', error.message, error);
					return { error: error.message };
				}
			},
			providesTags: ['Properties'],
		}),

		getProperty: builder.query<Property, string>({
			async queryFn(propertyId: string) {
				try {
					const docRef = doc(db, 'properties', propertyId);
					const docSnapshot = await getDoc(docRef);
					const data = docToData(docSnapshot) as Property;
					return { data };
				} catch (error: any) {
					return { error: error.message };
				}
			},
			async onCacheEntryAdded(
				propertyId,
				{ updateCachedData, cacheDataLoaded, cacheEntryRemoved },
			) {
				if (!propertyId) return;

				let unsubscribe: (() => void) | undefined;
				try {
					await cacheDataLoaded;

					unsubscribe = onSnapshot(
						doc(db, 'properties', propertyId),
						(docSnapshot) => {
							const latestData = docToData(docSnapshot) as Property | null;
							if (!latestData) return;

							updateCachedData((draft) => {
								Object.keys(draft as Record<string, unknown>).forEach((key) => {
									delete (draft as Record<string, unknown>)[key];
								});
								Object.assign(draft, latestData);
							});
						},
						(error) => {
							console.warn('Property live update listener failed:', error);
						},
					);

					await cacheEntryRemoved;
				} catch (error) {
					// The cache entry may be removed before the initial query resolves.
				} finally {
					unsubscribe?.();
				}
			},
			providesTags: ['Properties'],
		}),

		createProperty: builder.mutation<Property, Omit<Property, 'id'>>({
			async queryFn(newProperty) {
				try {
					let propertySequence = 1;
					const currentUser = auth.currentUser;
					if (!currentUser) {
						return { error: 'User not authenticated' };
					}
					const targetUserId = await resolveTargetUserId();
					const accessContext = await resolveAccountAccessContext();

					if (accessContext.isScopedTeamMember) {
						return {
							error:
								'Team member accounts cannot create properties. Ask the account owner to add properties and assign access.',
						};
					}

					await callFirebaseFunction<
						{
							accountId?: string;
							syncSubscription?: boolean;
							subscription?: Record<string, unknown>;
						},
						{
							id: string;
							subscription?: Record<string, unknown>;
						}
					>('ensureFamilyAccount', {
						accountId: targetUserId,
						syncSubscription: false,
					});

					const accountRef = doc(db, 'familyAccounts', targetUserId);
					const propertyRef = doc(collection(db, 'properties'));

					await runTransaction(db, async (transaction) => {
						const userSnapshot = await transaction.get(
							doc(db, 'users', targetUserId),
						);
						const userData = userSnapshot.data() || {};
						const subscription = userData.subscription || {};
						const accountSnapshot = await transaction.get(accountRef);
						const accountData = accountSnapshot.data() || {};
						const projection = accountData.effectiveEntitlementProjection || {};
						const subscriptionWithGrants = {
							...subscription,
							entitlementAccountId: targetUserId,
							entitlementGrants: Array.isArray(projection.activeGrants)
								? projection.activeGrants
								: [],
						};
						const planId = getEffectiveAccessPlanId(subscriptionWithGrants);
						const maxProperties = getMaxPropertiesForPlan(planId);
						const currentPropertyCount = Number(accountData.propertyCount || 0);
						propertySequence = currentPropertyCount + 1;

						if (currentPropertyCount >= maxProperties) {
							throw new Error(
								`Property limit reached for current plan (${maxProperties} max).`,
							);
						}

						const nowIso = new Date().toISOString();
						transaction.set(propertyRef, {
							...newProperty,
							userId: targetUserId,
							accountId: targetUserId,
							createdAt: nowIso,
							updatedAt: nowIso,
						});

						transaction.update(accountRef, {
							propertyCount: currentPropertyCount + 1,
							updatedAt: nowIso,
						});
					});

					if (newProperty.groupId) {
						try {
							await upsertPropertyGroupMembership({
								accountId: targetUserId,
								groupId: newProperty.groupId,
								propertyId: propertyRef.id,
								sortOrder: Date.now(),
							});
						} catch (membershipError) {
							console.warn(
								'Property created but group membership update failed:',
								membershipError,
							);
						}
					}

					const savedSnapshot = await getDoc(doc(db, 'properties', propertyRef.id));
					const savedData = docToData(savedSnapshot) as Property;
					void trackAnalyticsEvent('property_created', {
						action_source: 'user',
						property_sequence: propertySequence,
						property_type: String(savedData.propertyType || 'unspecified'),
						has_group: Boolean(savedData.groupId),
						has_notes: Boolean(String(savedData.notes || '').trim()),
					});
					return { data: savedData };
				} catch (error: any) {
					return { error: error.message };
				}
			},
			invalidatesTags: ['Properties', 'PropertyGroups'],
		}),

		updateProperty: builder.mutation<
			Property,
			{ id: string; updates: Partial<Property> }
		>({
			async queryFn({ id, updates }) {
				try {
					const docRef = doc(db, 'properties', id);
					const sanitizedUpdates =
						removeUndefinedFieldsDeep<Partial<Property>>(updates);
					const existingSnapshot = await getDoc(docRef);
					const existingData = existingSnapshot.data() || {};
					const accountId =
						String(existingData.accountId || '').trim() ||
						(await resolveTargetUserId());

					await updateDoc(docRef, {
						...sanitizedUpdates,
						updatedAt: new Date().toISOString(),
					});

					if ('groupId' in sanitizedUpdates) {
						if (sanitizedUpdates.groupId) {
							await upsertPropertyGroupMembership({
								accountId,
								groupId: sanitizedUpdates.groupId,
								propertyId: id,
							});
						} else {
							await deletePropertyGroupMemberships(id, accountId);
						}
					}

					const savedSnapshot = await getDoc(docRef);
					const savedData = docToData(savedSnapshot) as Property;
					return { data: savedData };
				} catch (error: any) {
					return { error: error.message };
				}
			},
			invalidatesTags: ['Properties', 'PropertyGroups'],
		}),

		deleteProperty: builder.mutation<void, string>({
			async queryFn(propertyId: string) {
				try {
					const currentUser = auth.currentUser;
					if (!currentUser) {
						return { error: 'User not authenticated' };
					}

					await callFirebaseFunction<
						{ propertyId: string },
						{ success: boolean }
					>('deletePropertyCascade', { propertyId });

					return { data: undefined };
				} catch (error: any) {
					return { error: error.message };
				}
			},
			invalidatesTags: [
				'Properties',
				'PropertyGroups',
				'Favorites',
				'Tasks',
				'Devices',
				'Suites',
				'Units',
				'Contractors',
				'MaintenanceHistory',
				'MaintenanceEvents',
			],
		}),

		// Legacy Unit readers preserve location context for existing records.
		getUnits: builder.query<Unit[], string>({
			async queryFn(propertyId: string) {
				try {
					if (!propertyId) {
						return { data: [] };
					}
					const accessContext = await resolveAccountAccessContext();
					if (
						accessContext.isScopedTeamMember &&
						!accessContext.allowedPropertyIds.includes(propertyId)
					) {
						return { data: [] };
					}
					const accessibleAccountIds = accessContext.accountIds;
					const units: Unit[] = [];
					for (const accountId of accessibleAccountIds) {
						const q = query(
							collection(db, 'units'),
							where('accountId', '==', accountId),
							where('propertyId', '==', propertyId),
						);
						const querySnapshot = await getDocs(q);
						const batch = querySnapshot.docs.map(
							(doc) => docToData(doc) as Unit,
						);
						units.push(...batch);
					}

					// First pass: de-dup exact records by document id.
					const uniqueById = Array.from(
						new Map(units.map((unit) => [unit.id, unit])).values(),
					) as Unit[];

					return { data: uniqueById };
				} catch (error) {
					return { error: (error as Error).message };
				}
			},
			providesTags: ['Units'],
		}),

		getUnit: builder.query<Unit, string>({
			async queryFn(unitId: string) {
				try {
					const docRef = doc(db, 'units', unitId);
					const docSnapshot = await getDoc(docRef);
					const data = docToData(docSnapshot) as Unit;
					return { data };
				} catch (error: any) {
					return { error: error.message };
				}
			},
			providesTags: ['Units'],
		}),

		// Get all units across all properties (for reports)
		getAllUnits: builder.query<Unit[], void>({
			async queryFn() {
				try {
					// Get authenticated user from Firebase Auth
					const currentUser = auth.currentUser;
					if (!currentUser) {
						return { error: 'User not authenticated' };
					}
					const accessContext = await resolveAccountAccessContext();
					const accessibleAccountIds = accessContext.accountIds;
					const units: Unit[] = [];
					for (const accountId of accessibleAccountIds) {
						const q = query(
							collection(db, 'units'),
							where('accountId', '==', accountId),
						);
						const querySnapshot = await getDocs(q);
						const batch = querySnapshot.docs.map(
							(doc) => docToData(doc) as Unit,
						);
						units.push(...batch);
					}
					const uniqueUnits = Array.from(
						new Map(units.map((unit) => [unit.id, unit])).values(),
					) as Unit[];
					return {
						data: filterRecordsByAccessProperties(
							uniqueUnits,
							accessContext,
							(unit) => unit.propertyId,
						),
					};
				} catch (error) {
					return { error: (error as Error).message };
				}
			},
			providesTags: ['Units'],
		}),
	}),
});

export const {
	useGetPropertyGroupsQuery,
	useGetPropertyGroupQuery,
	useCreatePropertyGroupMutation,
	useUpdatePropertyGroupMutation,
	useDeletePropertyGroupMutation,
	useGetPropertiesQuery,
	useGetPropertyQuery,
	useCreatePropertyMutation,
	useUpdatePropertyMutation,
	useDeletePropertyMutation,
	useGetUnitsQuery,
	useGetUnitQuery,
	useGetAllUnitsQuery,
} = propertySlice;
